// Chrome-less embeddable widget: a single-MLS, searchable slice of the
// Gitcha feed for MLS partner iframes. Net-new alongside IframeWidget.js
// (the plain list-only widget already embedded on partner sites) rather
// than a rewrite of it - existing embeds keep their current behavior
// untouched; partners opt into search via a distinct embed URL/snippet.
//
// Search/filter params are sent to the same public /wantads endpoint the
// plain widget uses. As of this writing the API only reads sortType/page/
// limit from that endpoint (Gitcha-API's widgetPublic.js) - the allowlist
// that makes search/filters actually narrow results is a separate,
// currently-unshipped API change. Until it ships, this widget will send
// correct requests that the server quietly ignores beyond sort/paging.
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { PropertyListing } from "../../components";
import useWidgetConfig from "../../hooks/useWidgetConfig";
import useWidgetFilterOptions from "../../hooks/useWidgetFilterOptions";
import useWidgetSearchFeed from "../../hooks/useWidgetSearchFeed";
import {
  createEmptyWidgetFilterState,
  computeActiveWidgetFilterCount,
  toggleWidgetFilterValue,
} from "../../helpers/widgetSearchFilters";
import { applyEmbedTheme } from "../../helpers/embedTheme";
import "./IframeWidgetSearch.scss";

const BLS_ROOT_DOMAIN = "thebuyerlistingservice.com";
const SEARCH_DEBOUNCE_MS = 400;

const FILTER_GROUP_LABELS = {
  propertyType: "Property type",
  financeType: "Financing",
  timeline: "Timeline",
  purchaseType: "Purchase type",
};

// The public widget feed only ever supports these two sorts
// (widgetPublic.js's ALLOWED_SORTS) - matching that here rather than
// offering options the server would silently clamp away.
const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
];

const tenantOrigin = (slug) => {
  const { protocol, host } = window.location;
  const root = host.includes("localhost")
    ? host.slice(host.indexOf("localhost"))
    : BLS_ROOT_DOMAIN;
  return `${protocol}//${slug}.${root}`;
};

const IframeWidgetSearch = () => {
  const { widgetKey } = useParams();

  const {
    data: config,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = useWidgetConfig(widgetKey);

  useEffect(() => {
    if (config?.tenant_slug) applyEmbedTheme(config.tenant_slug);
  }, [config]);

  const { data: filterOptions } = useWidgetFilterOptions();

  const [filterState, setFilterState] = useState(createEmptyWidgetFilterState);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [filtersExpanded, setFiltersExpanded] = useState(true);

  // Debounce the keyword box into filterState.search; flush on unmount so a
  // pending keystroke isn't lost if the visitor closes the embed mid-type.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilterState((prev) => ({ ...prev, search: searchInputValue }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInputValue]);

  const {
    data: feed,
    isLoading: isFeedLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useWidgetSearchFeed({ widgetKey, filterState, enabled: !!config });

  const ads = feed?.ads ?? [];
  const totalCount = feed?.totalCount ?? 0;
  const activeFilterCount = useMemo(
    () => computeActiveWidgetFilterCount(filterState),
    [filterState],
  );
  const hasActiveSearch = activeFilterCount > 0 || filterState.search.trim().length > 0;

  const toggleChip = (group, value) =>
    setFilterState((prev) => toggleWidgetFilterValue(prev, group, value));

  const clearAll = () => {
    setSearchInputValue("");
    setFilterState(createEmptyWidgetFilterState());
  };

  const openAd = (adSlug) => {
    if (adSlug && config?.tenant_slug)
      window.open(
        `${tenantOrigin(config.tenant_slug)}/want-listing-details/${adSlug}`,
        "_blank",
        "noopener",
      );
  };
  const handleItemClick = (_id, slug) => openAd(slug);
  const handleFavorite = (_isFav, wantAdId) =>
    openAd(ads.find((a) => a.Want_ad_Id === wantAdId)?.slug);

  if (isConfigLoading) {
    return (
      <div className="gitcha-embed-search-widget">
        <div className="gesw-state">Loading…</div>
      </div>
    );
  }

  if (isConfigError || !config) {
    return (
      <div className="gitcha-embed-search-widget">
        <div className="gesw-state">This widget is unavailable.</div>
      </div>
    );
  }

  const optionsFor = (group) => {
    const key = {
      propertyType: "propertyTypes",
      financeType: "financeTypes",
      timeline: "timelines",
      purchaseType: "purchaseTypes",
    }[group];
    return filterOptions?.[key] || [];
  };

  return (
    <div className="gitcha-embed-search-widget">
      <div className="gesw-head">
        <h2 className="gesw-title">Buyer Demand</h2>
        {config.market?.city && (
          <p className="gesw-sub">
            {config.market.city}
            {config.market.state ? `, ${config.market.state}` : ""}
          </p>
        )}
      </div>

      <div className="gesw-search">
        <svg
          className="gesw-search-icon"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="2" />
          <path d="m20 20-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          className="gesw-search-input"
          placeholder="Search by area, agent, or brokerage…"
          aria-label="Search by area, agent, or brokerage"
          value={searchInputValue}
          onChange={(e) => setSearchInputValue(e.target.value)}
        />
        {searchInputValue && (
          <button
            type="button"
            className="gesw-search-clear"
            aria-label="Clear search"
            onClick={() => setSearchInputValue("")}
          >
            ×
          </button>
        )}
      </div>

      <button
        type="button"
        className="gesw-filters-toggle"
        aria-expanded={filtersExpanded}
        onClick={() => setFiltersExpanded((v) => !v)}
      >
        <span>
          Filters
          {activeFilterCount > 0 && (
            <span className="gesw-filter-badge">{activeFilterCount}</span>
          )}
        </span>
        <span className={`gesw-chevron${filtersExpanded ? " gesw-chevron-open" : ""}`}>
          ▾
        </span>
      </button>

      {filtersExpanded && (
        <div className="gesw-filters">
          {Object.keys(FILTER_GROUP_LABELS).map((group) => {
            const options = optionsFor(group);
            if (!options.length) return null;
            return (
              <div className="gesw-filter-group" key={group}>
                <span className="gesw-filter-label">
                  {FILTER_GROUP_LABELS[group]}
                </span>
                <div className="gesw-chip-row">
                  {options.map((opt) => (
                    <button
                      type="button"
                      key={opt.id}
                      className="gesw-chip"
                      aria-pressed={filterState.selected[group].has(opt.id)}
                      onClick={() => toggleChip(group, opt.id)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          <div className="gesw-filter-group">
            <div className="gesw-chip-row">
              <button
                type="button"
                className="gesw-chip"
                aria-pressed={filterState.preApproved}
                onClick={() =>
                  setFilterState((prev) => ({
                    ...prev,
                    preApproved: !prev.preApproved,
                  }))
                }
              >
                Pre-approved only
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="gesw-toolbar">
        <div className="gesw-count">
          {totalCount} buyer{totalCount === 1 ? "" : "s"}
          {hasActiveSearch && (
            <button type="button" className="gesw-clear-link" onClick={clearAll}>
              Clear all
            </button>
          )}
        </div>
        <label className="gesw-sort">
          Sort
          <select
            value={filterState.sortType}
            onChange={(e) =>
              setFilterState((prev) => ({ ...prev, sortType: e.target.value }))
            }
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="gesw-list">
        {ads.length === 0 && !isFeedLoading ? (
          <div className="gesw-state">
            No buyer want-listings match these filters yet.
          </div>
        ) : (
          ads.map((ad) => (
            <PropertyListing
              key={ad.Want_ad_Id}
              list={ad}
              embedMode
              embedShareBaseUrl={tenantOrigin(config.tenant_slug)}
              handleWantAdItemClick={handleItemClick}
              handleFavoriteOrUnfavoriteWantAd={handleFavorite}
              onChangeText={() => {}}
              onAddClient={() => {}}
            />
          ))
        )}
        {(isFeedLoading || isFetchingNextPage) && (
          <div className="gesw-state">Loading…</div>
        )}
      </div>

      {hasNextPage && (
        <div className="gesw-footer">
          <button
            type="button"
            className="gesw-showmore"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            Show more of {totalCount} buyers
          </button>
        </div>
      )}
    </div>
  );
};

export default IframeWidgetSearch;
